import React, { useMemo } from 'react';
import RobotSettingsInput from './robotSettingsInput';
import { CapturePropertyRobotDTO } from 'business/models/Models';
import {
  FixedLightPathOptions,
  fluorescenceModePropName,
  frapImagingModes,
  frapPrefix,
} from '../constants';
import FRAPCustomControls from './FRAPCustomControls';
import { Field, FrapConfigType } from '../types';

interface RobotSettingsFormProps {
  opticsFields: string[];
  robotCaptureProperties: CapturePropertyRobotDTO;
  robotConfig: string;
  lightPath: string;
  selectedCp: number[];
  robotId: number;
  handleChange: (propertyName: string, value: any) => void;
  handleFieldToggle: (propertyName: string, value?: any) => void;
  enabledFields: string[];
  frapConfig?: FrapConfigType;
}

function RobotSettingsForm(props: RobotSettingsFormProps) {
  const {
    lightPath,
    opticsFields,
    robotConfig,
    robotCaptureProperties,
    robotId,
    selectedCp,
    handleChange,
    handleFieldToggle,
    enabledFields,
    frapConfig,
  } = props;

  if (
    !lightPath ||
    lightPath === FixedLightPathOptions.Disabled ||
    !robotCaptureProperties
  ) {
    return null;
  }

  const opticsControls = robotConfig[lightPath]?.some((x) =>
    opticsFields.includes(x.DisplayLabel),
  );

  const getFrapControls = () => {
    if (
      robotConfig[lightPath]?.some((x) =>
        x.PropertyName.includes(fluorescenceModePropName),
      ) &&
      (robotCaptureProperties?.data[robotId]?.[fluorescenceModePropName] ||
        robotCaptureProperties?.data[robotId]?.[
          frapPrefix + fluorescenceModePropName
        ]) === frapImagingModes.FRAP
    ) {
      return (
        <FRAPCustomControls
          opticsFields={opticsFields}
          robotCaptureProperties={robotCaptureProperties}
          robotConfig={robotConfig}
          lightPath={lightPath}
          selectedCp={selectedCp}
          robotId={robotId}
          handleChange={handleChange}
          handleFieldToggle={handleFieldToggle}
          enabledFields={enabledFields}
          frapConfig={frapConfig}
        />
      );
    }
  };

  const duplicateFields = useMemo(() => {
    const obj = robotConfig[lightPath]?.reduce((a, c: Field) => {
      a[c.PropertyName] ? a[c.PropertyName]++ : (a[c.PropertyName] = 1);
      return a;
    }, {});
    return Object.keys(obj)?.filter((x) => obj[x] > 1);
  }, [robotConfig, lightPath]);

  return (
    <div id="robotsettingsform-wrapper">
      <fieldset id="robotsettingsform-fieldset" disabled={!selectedCp.length}>
        <div
          id="robotsettingsform-opticscontrol-wrapper"
          className="flex flex-row gap-x-12 gap-y-2 flex-wrap"
        >
          {opticsControls ? (
            <>
              <legend id="robotsettingsform-opticscontrol-title" className="text-lg">
                Optics Control
              </legend>
              {robotConfig[lightPath]?.map((field: Field, index: number) => {
                if (opticsFields.includes(field.DisplayLabel)) {
                  return (
                    <RobotSettingsInput
                      key={index}
                      field={field}
                      value={robotCaptureProperties.data[robotId]?.[field.PropertyName]}
                      onChange={handleChange}
                      onToggle={handleFieldToggle}
                      enabled={enabledFields.includes(field.PropertyName)}
                      formData={robotCaptureProperties.data[robotId]}
                      hasDuplicate={duplicateFields?.includes(field.PropertyName)}
                    />
                  );
                }
                return null;
              })}
            </>
          ) : null}
          {(robotCaptureProperties?.data[robotId]?.[fluorescenceModePropName] ||
            robotCaptureProperties?.data[robotId]?.[
              frapPrefix + fluorescenceModePropName
            ]) !== frapImagingModes.FRAP ? (
            <legend id="robotsettingsform-cameracontrol-title" className="text-lg">
              Camera Control
            </legend>
          ) : null}
          {robotConfig[lightPath]?.map((field: Field, index: number) => {
            if (!opticsFields.includes(field.DisplayLabel)) {
              return (
                <RobotSettingsInput
                  key={index}
                  field={field}
                  value={robotCaptureProperties.data[robotId]?.[field.PropertyName]}
                  onChange={handleChange}
                  onToggle={handleFieldToggle}
                  enabled={enabledFields.includes(field.PropertyName)}
                  formData={robotCaptureProperties.data[robotId]}
                  hasDuplicate={duplicateFields?.includes(field.PropertyName)}
                />
              );
            }
            return null;
          })}
        </div>
        {getFrapControls()}
      </fieldset>
    </div>
  );
}

export default RobotSettingsForm;
